import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import { Bar, Pie, Line } from 'react-chartjs-2';
import { authBaseUrl, metricsBaseUrl, projectMetricsBaseUrl } from '../config/runtime';
import { formatDuration } from '../utils/duration';
import './Dashboard.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend, ArcElement);

const Dashboard = () => {
  const [summary, setSummary] = useState({
    totalAtendimentos: 0,
    totalAbertos: 0,
    totalFinalizados: 0,
    tempoMedioComRobo: 0,
    tempoMedioComAtendente: 0,
    tempoMedioTotal: 0,
    tempoMedioEspera: 0,
    statusDistribuicao: {},
    atendimentosPorAtendente: {},
    atendimentosPorData: {},
  });
  const [projectSummary, setProjectSummary] = useState({
    kpis: {
      total_projects: 0,
      open_projects: 0,
      completed_projects: 0,
      completion_rate: 0,
      completed_on_time_rate: 0,
      overdue_open_projects: 0,
      open_sla_breached: 0,
      avg_lead_time: 0,
      avg_cycle_time: 0,
      avg_open_age: 0,
    },
    status_distribution: {},
    priority_distribution: {},
    stage_distribution: {},
    workspace_distribution: {},
    backlog_by_stage: {},
    throughput_by_date: {},
  });

  const [periodo, setPeriodo] = useState(() => {
    const today = new Date();
    const end = today.toISOString().slice(0, 10);
    const startDate = new Date();
    startDate.setDate(today.getDate() - 6);
    const start = startDate.toISOString().slice(0, 10);
    return { inicio: start, fim: end };
  });

  const [shouldFetchData, setShouldFetchData] = useState(false);
  const [activeDashboard, setActiveDashboard] = useState('multi');
  const [attendants, setAttendants] = useState([]);
  const [attendantFilter, setAttendantFilter] = useState('all');
  const [loadError, setLoadError] = useState('');

  const handlePeriodChange = (e) => {
    setPeriodo({
      ...periodo,
      [e.target.name]: e.target.value
    });
  };

  const handleFilterClick = () => {
    setShouldFetchData(true);
  };

  const handleLast7Days = () => {
    const today = new Date();
    const end = today.toISOString().slice(0, 10);
    const startDate = new Date();
    startDate.setDate(today.getDate() - 6);
    const start = startDate.toISOString().slice(0, 10);
    setPeriodo({ inicio: start, fim: end });
    setShouldFetchData(true);
  };

  useEffect(() => {
    const loadAssignableUsers = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setAttendants([]);
        return;
      }
      try {
        const companyCode = localStorage.getItem('companyCode');
        const res = await axios.get(`${authBaseUrl}/users/assignable`, {
          headers: {
            Authorization: `Bearer ${token}`,
            ...(companyCode ? { 'X-Company-Code': companyCode } : {}),
          },
        });
        const rows = Array.isArray(res.data) ? res.data : [];
        setAttendants(rows.filter((row) => row?.email));
      } catch (error) {
        setAttendants([]);
      }
    };
    loadAssignableUsers();
  }, []);

  const fetchData = useCallback(() => {
    const atendimentoParams = new URLSearchParams();
    const projetosParams = new URLSearchParams();
    if (periodo.inicio && periodo.fim) {
      atendimentoParams.set('inicio', periodo.inicio);
      atendimentoParams.set('fim', periodo.fim);
      projetosParams.set('inicio', periodo.inicio);
      projetosParams.set('fim', periodo.fim);
    }
    if (attendantFilter === 'me') {
      const currentUser = localStorage.getItem('userId');
      if (currentUser) {
        atendimentoParams.set('user_id', currentUser);
      }
    } else if (attendantFilter !== 'all') {
      atendimentoParams.set('user_id', attendantFilter);
    }

    const atendimentoUrl = atendimentoParams.toString()
      ? `${metricsBaseUrl}?${atendimentoParams.toString()}`
      : metricsBaseUrl;
    const projetosUrl = projetosParams.toString()
      ? `${projectMetricsBaseUrl}?${projetosParams.toString()}`
      : projectMetricsBaseUrl;

    Promise.allSettled([axios.get(atendimentoUrl), axios.get(projetosUrl)])
      .then(([atendimentoResult, projetosResult]) => {
        let atendimentoFailed = false;
        let projetosFailed = false;

        if (atendimentoResult.status === 'fulfilled') {
          const data = Array.isArray(atendimentoResult.value?.data) ? atendimentoResult.value.data : [];
          const totalAtendimentos = data.length;
          const totalFinalizados = data.filter((metric) =>
            ['finalizado', 'encerrado', 'resolvido'].includes(metric.status_final)
          ).length;
          const totalAbertos = totalAtendimentos - totalFinalizados;

          const somaTempoComRobo = data.reduce((sum, metric) => sum + (Number(metric.tempo_com_robo) || 0), 0);
          const somaTempoComAtendente = data.reduce((sum, metric) => sum + (Number(metric.tempo_com_atendente) || 0), 0);
          const somaTempoTotal = data.reduce((sum, metric) => sum + (Number(metric.tempo_total_atendimento) || 0), 0);
          const somaTempoEspera = data.reduce((sum, metric) => sum + (Number(metric.tempo_espera) || 0), 0);

          const statusDistribuicao = data.reduce((acc, metric) => {
            const key = metric.status_final || 'sem_status';
            acc[key] = (acc[key] || 0) + 1;
            return acc;
          }, {});

          const atendimentosPorAtendente = data.reduce((acc, metric) => {
            const key = metric.atendente || 'Sem atendente';
            acc[key] = (acc[key] || 0) + 1;
            return acc;
          }, {});

          const atendimentosPorData = data.reduce((acc, metric) => {
            const key = metric.data_atendimento || 'sem_data';
            acc[key] = (acc[key] || 0) + 1;
            return acc;
          }, {});

          setSummary({
            totalAtendimentos,
            totalAbertos,
            totalFinalizados,
            tempoMedioComRobo: totalAtendimentos ? somaTempoComRobo / totalAtendimentos : 0,
            tempoMedioComAtendente: totalAtendimentos ? somaTempoComAtendente / totalAtendimentos : 0,
            tempoMedioTotal: totalAtendimentos ? somaTempoTotal / totalAtendimentos : 0,
            tempoMedioEspera: totalAtendimentos ? somaTempoEspera / totalAtendimentos : 0,
            statusDistribuicao,
            atendimentosPorAtendente,
            atendimentosPorData,
          });
        } else {
          atendimentoFailed = true;
        }

        if (projetosResult.status === 'fulfilled') {
          const projetosData = projetosResult.value?.data || {};
          setProjectSummary({
            kpis: {
              total_projects: Number(projetosData?.kpis?.total_projects || 0),
              open_projects: Number(projetosData?.kpis?.open_projects || 0),
              completed_projects: Number(projetosData?.kpis?.completed_projects || 0),
              completion_rate: Number(projetosData?.kpis?.completion_rate || 0),
              completed_on_time_rate: Number(projetosData?.kpis?.completed_on_time_rate || 0),
              overdue_open_projects: Number(projetosData?.kpis?.overdue_open_projects || 0),
              open_sla_breached: Number(projetosData?.kpis?.open_sla_breached || 0),
              avg_lead_time: Number(projetosData?.kpis?.avg_lead_time || 0),
              avg_cycle_time: Number(projetosData?.kpis?.avg_cycle_time || 0),
              avg_open_age: Number(projetosData?.kpis?.avg_open_age || 0),
            },
            status_distribution: projetosData?.status_distribution || {},
            priority_distribution: projetosData?.priority_distribution || {},
            stage_distribution: projetosData?.stage_distribution || {},
            workspace_distribution: projetosData?.workspace_distribution || {},
            backlog_by_stage: projetosData?.backlog_by_stage || {},
            throughput_by_date: projetosData?.throughput_by_date || {},
          });
        } else {
          projetosFailed = true;
        }

        if (atendimentoFailed && projetosFailed) {
          setLoadError('Nao foi possivel carregar as metricas do dashboard.');
        } else if (atendimentoFailed) {
          setLoadError('Metricas de Multi Atendimento indisponiveis no momento.');
        } else if (projetosFailed) {
          setLoadError('Metricas de Projetos indisponiveis no momento.');
        } else {
          setLoadError('');
        }
      })
      .catch(() => {
        setLoadError('Falha ao carregar dados do dashboard.');
      })
      .finally(() => {
        setShouldFetchData(false);
      });
  }, [periodo.inicio, periodo.fim, attendantFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (shouldFetchData) {
      fetchData();
    }
  }, [shouldFetchData, fetchData]);

  const formatPercent = (value) => `${Number(value || 0).toFixed(1)}%`;

  const chartColors = {
    primary: '#B10905',
    wine: '#65202A',
    graphite: '#0f0f0f',
    mist: '#ECECEC',
    accent: '#FF8B3D',
    cool: '#3FA9F5',
  };

  const commonOptions = {
    responsive: true,
    plugins: {
      legend: {
        labels: {
          color: '#ECECEC',
          font: { family: 'Space Grotesk', size: 12, weight: '600' },
        },
      },
      tooltip: {
        backgroundColor: 'rgba(0,0,0,0.85)',
        borderColor: 'rgba(177, 9, 5, 0.4)',
        borderWidth: 1,
        titleColor: '#FFFFFF',
        bodyColor: '#ECECEC',
      },
    },
    scales: {
      x: {
        grid: { color: 'rgba(236,236,236,0.08)' },
        ticks: { color: 'rgba(236,236,236,0.8)' },
      },
      y: {
        grid: { color: 'rgba(236,236,236,0.08)' },
        ticks: { color: 'rgba(236,236,236,0.8)' },
        beginAtZero: true,
      },
    },
  };

  const durationOptions = {
    ...commonOptions,
    scales: {
      ...commonOptions.scales,
      y: {
        ...commonOptions.scales.y,
        ticks: {
          ...commonOptions.scales.y.ticks,
          callback: (value) => formatDuration(Number(value)),
        },
      },
    },
    plugins: {
      ...commonOptions.plugins,
      tooltip: {
        ...commonOptions.plugins.tooltip,
        callbacks: {
          label: (context) => {
            const value = context.parsed?.y ?? context.parsed ?? 0;
            return `${context.dataset.label}: ${formatDuration(Number(value))}`;
          },
        },
      },
    },
  };

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <div>
          <h1>Dashboard de Métricas</h1>
          <p>Visão rápida do atendimento e eficiência operacional.</p>
        </div>
      </div>
      {loadError && <p className="dashboard-load-error">{loadError}</p>}

      <div className="filter">
        <label>
          Data de Início:
          <input type="date" name="inicio" value={periodo.inicio} onChange={handlePeriodChange} />
        </label>
        <label>
          Data de Fim:
          <input type="date" name="fim" value={periodo.fim} onChange={handlePeriodChange} />
        </label>
        <button onClick={handleFilterClick}>Filtrar</button>
        <button className="secondary" onClick={handleLast7Days}>Últimos 7 dias</button>
        <label>
          Atendente:
          <select
            value={attendantFilter}
            onChange={(event) => setAttendantFilter(event.target.value)}
          >
            <option value="all">Todos</option>
            <option value="me">Meu usuário</option>
            {attendants.map((attendant) => (
              <option key={attendant.email} value={attendant.email}>
                {attendant.name || attendant.email}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="dashboard-tabs">
        <button
          className={activeDashboard === 'multi' ? 'active' : ''}
          onClick={() => setActiveDashboard('multi')}
        >
          Multi Atendimento
        </button>
        <button
          className={activeDashboard === 'projects' ? 'active' : ''}
          onClick={() => setActiveDashboard('projects')}
        >
          Projetos
        </button>
      </div>

      {activeDashboard === 'multi' && (
      <>
      <h2 className="dashboard-section-title">Multi Atendimento</h2>
      <div className="kpi-grid">
        <div className="kpi-card">
          <span>Total de Atendimentos</span>
          <strong>{summary.totalAtendimentos}</strong>
        </div>
        <div className="kpi-card">
          <span>Chamados Abertos</span>
          <strong>{summary.totalAbertos}</strong>
        </div>
        <div className="kpi-card">
          <span>Chamados Finalizados</span>
          <strong>{summary.totalFinalizados}</strong>
        </div>
        <div className="kpi-card">
          <span>Tempo Médio com Robô</span>
          <strong>{formatDuration(summary.tempoMedioComRobo)}</strong>
        </div>
        <div className="kpi-card">
          <span>Tempo Médio com Atendente</span>
          <strong>{formatDuration(summary.tempoMedioComAtendente)}</strong>
        </div>
        <div className="kpi-card">
          <span>Tempo Médio Total</span>
          <strong>{formatDuration(summary.tempoMedioTotal)}</strong>
        </div>
        <div className="kpi-card">
          <span>Tempo Médio de Espera</span>
          <strong>{formatDuration(summary.tempoMedioEspera)}</strong>
        </div>
      </div>

      <div className="charts">
        <div className="chart-container">
          <h3>Total de Atendimentos</h3>
          <Bar
            data={{
              labels: ['Total de Atendimentos'],
              datasets: [
                {
                  label: 'Atendimentos',
                  data: [summary.totalAtendimentos],
                  backgroundColor: chartColors.primary,
                  borderRadius: 10,
                  borderWidth: 0,
                },
              ],
            }}
            options={commonOptions}
          />
        </div>
        <div className="chart-container">
          <h3>Tempo Médio com Robô</h3>
          <Line
            data={{
              labels: ['Tempo Médio com Robô'],
              datasets: [
                {
                  label: 'Tempo Médio com Robô',
                  data: [summary.tempoMedioComRobo],
                  backgroundColor: chartColors.cool,
                  borderColor: chartColors.cool,
                  fill: true,
                  tension: 0.4,
                  pointRadius: 4,
                },
              ],
            }}
            options={durationOptions}
          />
        </div>
        <div className="chart-container">
          <h3>Tempo Médio com Atendente</h3>
          <Line
            data={{
              labels: ['Tempo Médio com Atendente'],
              datasets: [
                {
                  label: 'Tempo Médio com Atendente',
                  data: [summary.tempoMedioComAtendente],
                  backgroundColor: chartColors.wine,
                  borderColor: chartColors.wine,
                  fill: true,
                  tension: 0.4,
                  pointRadius: 4,
                },
              ],
            }}
            options={durationOptions}
          />
        </div>
        <div className="chart-container">
          <h3>Tempo Médio Total de Atendimento</h3>
          <Line
            data={{
              labels: ['Tempo Médio Total'],
              datasets: [
                {
                  label: 'Tempo Médio Total de Atendimento',
                  data: [summary.tempoMedioTotal],
                  backgroundColor: chartColors.accent,
                  borderColor: chartColors.accent,
                  fill: true,
                  tension: 0.4,
                  pointRadius: 4,
                },
              ],
            }}
            options={durationOptions}
          />
        </div>
        <div className="chart-container">
          <h3>Tempo Médio de Espera</h3>
          <Line
            data={{
              labels: ['Tempo Médio de Espera'],
              datasets: [
                {
                  label: 'Tempo Médio de Espera',
                  data: [summary.tempoMedioEspera],
                  backgroundColor: chartColors.primary,
                  borderColor: chartColors.primary,
                  fill: true,
                  tension: 0.4,
                  pointRadius: 4,
                },
              ],
            }}
            options={durationOptions}
          />
        </div>
        <div className="chart-container">
          <h3>Distribuição de Status Final</h3>
          <Bar
            data={{
              labels: Object.keys(summary.statusDistribuicao),
              datasets: [
                {
                  label: 'Status Final',
                  data: Object.values(summary.statusDistribuicao),
                  backgroundColor: chartColors.wine,
                  borderRadius: 10,
                },
              ],
            }}
            options={commonOptions}
          />
        </div>
        <div className="chart-container">
          <h3>Atendimentos por Atendente</h3>
          <Bar
            data={{
              labels: Object.keys(summary.atendimentosPorAtendente),
              datasets: [
                {
                  label: 'Atendimentos por Atendente',
                  data: Object.values(summary.atendimentosPorAtendente),
                  backgroundColor: chartColors.cool,
                  borderRadius: 10,
                },
              ],
            }}
            options={commonOptions}
          />
        </div>
        <div className="chart-container">
          <h3>Atendimentos por Data</h3>
          <Line
            data={{
              labels: Object.keys(summary.atendimentosPorData),
              datasets: [
                {
                  label: 'Atendimentos por Data',
                  data: Object.values(summary.atendimentosPorData),
                  backgroundColor: chartColors.wine,
                  borderColor: chartColors.wine,
                  fill: true,
                  tension: 0.35,
                  pointRadius: 3,
                },
              ],
            }}
            options={{
              ...commonOptions,
              scales: {
                ...commonOptions.scales,
                x: {
                  ...commonOptions.scales.x,
                  ticks: {
                    ...commonOptions.scales.x.ticks,
                    maxRotation: 45,
                    minRotation: 45,
                    autoSkip: true,
                    maxTicksLimit: 10,
                    font: { size: 12 },
                  },
                },
              },
            }}
          />
        </div>
        <div className="chart-container">
          <h3>Comparação Tempo Médio (Robô vs Atendente)</h3>
          <Pie
            data={{
              labels: ['Tempo com Robô', 'Tempo com Atendente'],
              datasets: [
                {
                  data: [summary.tempoMedioComRobo, summary.tempoMedioComAtendente],
                  backgroundColor: [chartColors.primary, chartColors.cool],
                  borderColor: chartColors.graphite,
                  borderWidth: 2,
                },
              ],
            }}
            options={{
              ...commonOptions,
              plugins: {
                ...commonOptions.plugins,
                legend: { position: 'top' },
                tooltip: {
                  ...commonOptions.plugins.tooltip,
                  callbacks: {
                    label: (context) => `${context.label}: ${formatDuration(Number(context.parsed || 0))}`,
                  },
                },
              }
            }}
          />
        </div>
      </div>
      </>
      )}

      {activeDashboard === 'projects' && (
      <>
      <h2 className="dashboard-section-title">Projetos (ITIL)</h2>
      <div className="kpi-grid">
        <div className="kpi-card">
          <span>Total de Projetos</span>
          <strong>{projectSummary.kpis.total_projects}</strong>
        </div>
        <div className="kpi-card">
          <span>Backlog Aberto</span>
          <strong>{projectSummary.kpis.open_projects}</strong>
        </div>
        <div className="kpi-card">
          <span>Concluídos</span>
          <strong>{projectSummary.kpis.completed_projects}</strong>
        </div>
        <div className="kpi-card">
          <span>Taxa de Conclusão</span>
          <strong>{formatPercent(projectSummary.kpis.completion_rate)}</strong>
        </div>
        <div className="kpi-card">
          <span>Concluídos no Prazo</span>
          <strong>{formatPercent(projectSummary.kpis.completed_on_time_rate)}</strong>
        </div>
        <div className="kpi-card">
          <span>Abertos em Atraso</span>
          <strong>{projectSummary.kpis.overdue_open_projects}</strong>
        </div>
        <div className="kpi-card">
          <span>SLA Violado (Abertos)</span>
          <strong>{projectSummary.kpis.open_sla_breached}</strong>
        </div>
        <div className="kpi-card">
          <span>Lead Time Médio</span>
          <strong>{formatDuration(projectSummary.kpis.avg_lead_time)}</strong>
        </div>
        <div className="kpi-card">
          <span>Cycle Time Médio</span>
          <strong>{formatDuration(projectSummary.kpis.avg_cycle_time)}</strong>
        </div>
        <div className="kpi-card">
          <span>Idade Média do Backlog</span>
          <strong>{formatDuration(projectSummary.kpis.avg_open_age)}</strong>
        </div>
      </div>

      <div className="charts">
        <div className="chart-container">
          <h3>Projetos por Status</h3>
          <Bar
            data={{
              labels: Object.keys(projectSummary.status_distribution),
              datasets: [
                {
                  label: 'Projetos',
                  data: Object.values(projectSummary.status_distribution),
                  backgroundColor: chartColors.wine,
                  borderRadius: 10,
                },
              ],
            }}
            options={commonOptions}
          />
        </div>
        <div className="chart-container">
          <h3>Distribuição por Prioridade</h3>
          <Pie
            data={{
              labels: Object.keys(projectSummary.priority_distribution),
              datasets: [
                {
                  data: Object.values(projectSummary.priority_distribution),
                  backgroundColor: [chartColors.primary, chartColors.cool, chartColors.accent, chartColors.wine, chartColors.mist],
                  borderColor: chartColors.graphite,
                  borderWidth: 2,
                },
              ],
            }}
            options={{
              ...commonOptions,
              plugins: {
                ...commonOptions.plugins,
                legend: { position: 'top' },
              },
            }}
          />
        </div>
        <div className="chart-container">
          <h3>Backlog por Etapa</h3>
          <Bar
            data={{
              labels: Object.keys(projectSummary.backlog_by_stage),
              datasets: [
                {
                  label: 'Backlog Aberto',
                  data: Object.values(projectSummary.backlog_by_stage),
                  backgroundColor: chartColors.primary,
                  borderRadius: 10,
                },
              ],
            }}
            options={commonOptions}
          />
        </div>
        <div className="chart-container">
          <h3>Throughput de Conclusão (Dia)</h3>
          <Line
            data={{
              labels: Object.keys(projectSummary.throughput_by_date),
              datasets: [
                {
                  label: 'Projetos Concluídos',
                  data: Object.values(projectSummary.throughput_by_date),
                  backgroundColor: chartColors.cool,
                  borderColor: chartColors.cool,
                  fill: true,
                  tension: 0.35,
                  pointRadius: 3,
                },
              ],
            }}
            options={commonOptions}
          />
        </div>
      </div>
      </>
      )}
    </div>
  );
};

export default Dashboard;
